import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Container, Row, Col, Table, Alert, Spinner, Card, Button, Form, InputGroup, Badge } from 'react-bootstrap';
import Navbar from '../components/Navbar';
import { ThemeContext } from '../context/ThemeContext';
import { DestinazioneContext } from '../context/DestinazioneContext';
import { useAlert } from '../context/AlertContext';
import configService from '../services/configService';
import useIsMobile from '../hooks/useIsMobile';

const Preordini = () => {
  const { darkMode } = useContext(ThemeContext);
  const { destinazione } = useContext(DestinazioneContext);
  const { showAlertGlobal } = useAlert();
  
  const [preordini, setPreordini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [codiceFilter, setCodiceFilter] = useState('');
  const [onlySuWeb, setOnlySuWeb] = useState(false);
  const [onlySuFlash, setOnlySuFlash] = useState(false);
  const isMobile = useIsMobile();
  
  const [periodo, setPeriodo] = useState(null);
  
  const getItalianDate = () => {
    const date = new Date();
    const options = { timeZone: 'Europe/Rome' };
    const italianDateString = date.toLocaleString('en-US', options);
    return new Date(italianDateString);
  };

  // Calcola la data di domani
  const getTomorrowDate = () => {
    const tomorrow = getItalianDate();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toLocaleDateString("it-IT", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).split("/").reverse().join("-");
  };
  
  // Calcola il giorno della settimana (1-7, dove lunedì = 1)
  const getWeekDay = (date) => {
    const day = new Date(date).getDay();
    return day === 0 ? 7 : day; // Converte domenica da 0 a 7
  };
  
  const [filtri, setFiltri] = useState({
    giorno_esatto: getTomorrowDate(),
    giorno_fine: getTomorrowDate(),
    gsettimana: getWeekDay(getTomorrowDate()),
    optiongroup2: 1,
    filterDescrizione: "",
    filterCodiceArti: "",
    filterClienteDest: "",
    filterCat1: "",
    filterCat3: "",
    ldata_ex: 0,
    xmacli: ""
  });

  // Carica i preordini
  const loadPreordini = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Formatto il body con chiavi tra apici singoli come nell'orderService
      // NON inviare searchFilter e codiceFilter al backend - verranno applicati lato frontend
      const body = {
        'giorno_esatto': filtri.giorno_esatto,
        'giorno_fine': filtri.giorno_fine,
        'gsettimana': filtri.gsettimana,
        'optiongroup2': filtri.optiongroup2,
        'filterDescrizione': '', // Lascia vuoto per ottenere tutti i dati
        'filterCodiceArti': '', // Lascia vuoto per ottenere tutti i dati
        'filterClienteDest': filtri.filterClienteDest,
        'filterCat1': filtri.filterCat1,
        'filterCat3': filtri.filterCat3,
        'ldata_ex': filtri.ldata_ex,
        'xmacli': filtri.xmacli
      };
      
      const response = await configService.getPreordini(body);
      
      if (response && response.data) {
        // Controlla se i dati sono in response.data.articoli o simile
        const articoli = response.data.articoli || response.data.preordini || response.data;
       
        
        setPreordini(Array.isArray(articoli) ? articoli : []);
        setPeriodo(response.data.periodo || null);
        
        if (articoli && articoli.length > 0) {
        
        }
      } else {
        setPreordini([]);
        setPeriodo(null);
      }
      
    } catch (error) {
      
      setError(`Errore nel caricamento dei preordini: ${error.message}`);
      showAlertGlobal("Errore nel caricamento dei preordini", "danger");
    } finally {
      setLoading(false);
    }
  }, [filtri, searchFilter, codiceFilter, showAlertGlobal]);

  // Carica i preordini quando cambia destinazione o filtri
  useEffect(() => {
    if (destinazione) {
      loadPreordini();
    }
  }, [destinazione, loadPreordini]);

  // Filtro preordini in tempo reale
  const filteredPreordini = useMemo(() => {
   
    
    if (preordini.length > 0) {
      
    }
    
    return preordini.filter(preordine => {
      // Normalizza i valori e gestisci null/undefined
      const descrizione = (preordine.descriz || '').toLowerCase().trim();
      const codice = (preordine.codice_prod || '').toLowerCase().trim();
      const searchDesc = searchFilter.toLowerCase().trim();
      const searchCod = codiceFilter.toLowerCase().trim();
      const matchDescrizione =
        searchDesc === '' ||
        descrizione.includes(searchDesc) ||
        codice.includes(searchDesc);
      const matchCodice = searchCod === '' || codice.includes(searchCod);

      if (onlySuWeb) {
        const flag = preordine?.su_web ?? preordine?.suWeb ?? preordine?.SU_WEB ?? '0';
        if (String(flag).trim() !== '1') return false;
      }
      if (onlySuFlash) {
        const flag = preordine?.su_flash ?? preordine?.suFlash ?? preordine?.SU_FLASH ?? '0';
        if (String(flag).trim() !== '1') return false;
      }
      
      return matchDescrizione && matchCodice;
    });
  }, [preordini, searchFilter, codiceFilter, onlySuWeb, onlySuFlash]);

  const hasActiveFilters =
    searchFilter || codiceFilter || onlySuWeb || onlySuFlash;

  // Formatta i numeri con massimo 3 decimali
  const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return num.toFixed(3).replace(/\.?0+$/, ''); // Rimuove zeri finali non necessari
  };

  // Formatta teglie_info: il valore dopo il + viene arrotondato all'intero. Se è 0 o negativo, viene omesso.
  const formatTeglieInfo = (teglieInfo) => {
    if (!teglieInfo || typeof teglieInfo !== 'string') return teglieInfo || '';
    // Formato: "X x Y + Z" (es. "10 x 20 + 15.333333" o "6 x 1 + 0" o "1 x 8 + -4")
    const match = teglieInfo.match(/^(.+?)\s*\+\s*(-?[\d.,]+)\s*$/);
    if (match) {
      const partePrima = match[1].trim();
      const parteDopo = parseFloat(String(match[2]).replace(',', '.'));
      if (!isNaN(parteDopo)) {
        if (parteDopo <= 0) {
          return partePrima; // "6 x 1 + 0" → "6 x 1", "1 x 8 + -4" → "1 x 8"
        }
        return `${partePrima} + ${Math.round(parteDopo)}`;
      }
    }
    return teglieInfo;
  };

  // Gestisce il cambiamento delle date
  const handleDateChange = (field, value) => {
    const newFiltri = { ...filtri, [field]: value };
    
    // Se cambia giorno_esatto, aggiorna anche gsettimana
    if (field === 'giorno_esatto') {
      newFiltri.gsettimana = getWeekDay(value);
    }
    
    setFiltri(newFiltri);
  };

  // Formatta la data per visualizzazione
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT');
  };

  return (
    <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
      <Navbar />
      
      {/* RENDERING CONDIZIONALE BASATO SU JAVASCRIPT */}
      {!isMobile ? (
        // ===== VERSIONE DESKTOP =====
        <Container fluid className="mt-5 pt-3">
          <Row className="justify-content-center">
            <Col xs={12}>
              
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h2 className={darkMode ? 'text-light' : 'text-dark'}>
                    <i className="fas fa-clipboard-list me-2"></i>
                    Lista di Produzione
                  </h2>
                  {periodo && (
                    <p className={`mb-0 ${darkMode ? 'text-light' : 'text-muted'}`}>
                      Periodo: {formatDate(periodo.data_inizio)} - {formatDate(periodo.data_fine)} 
                      ({periodo.giorni_elaborati} giorni elaborati)
                    </p>
                  )}
                </div>
              </div>

              {/* Filtri */}
              <Card className="mb-4">
                <Card.Header className="card-header-sidebar-bg">
                  <h5 className="mb-0">
                    <i className="fas fa-filter me-2"></i>Filtri
                  </h5>
                </Card.Header>
                <Card.Body className={darkMode ? 'text-light' : ''}>
                  <Row className="g-3">
                    {/* Date */}
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Data Inizio</Form.Label>
                        <Form.Control
                          type="date"
                          value={filtri.giorno_esatto}
                          onChange={(e) => handleDateChange('giorno_esatto', e.target.value)}
                          className={darkMode ? "bg-dark text-light border-secondary" : ""}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Data Fine</Form.Label>
                        <Form.Control
                          type="date"
                          value={filtri.giorno_fine}
                          onChange={(e) => handleDateChange('giorno_fine', e.target.value)}
                          className={darkMode ? "bg-dark text-light border-secondary" : ""}
                        />
                      </Form.Group>
                    </Col>
                    
                    {/* Filtri testo */}
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Filtra per Descrizione</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className={darkMode ? "bg-dark text-light border-secondary" : ""}>
                            <i className="fas fa-search"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            placeholder="Cerca descrizioni..."
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            className={darkMode ? "bg-dark text-light border-secondary" : ""}
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Filtra per Codice</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className={darkMode ? "bg-dark text-light border-secondary" : ""}>
                            <i className="fas fa-hashtag"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            placeholder="Cerca codici..."
                            value={codiceFilter}
                            onChange={(e) => setCodiceFilter(e.target.value)}
                            className={darkMode ? "bg-dark text-light border-secondary" : ""}
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="mt-3">
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                      <Form.Check
                        type="switch"
                        id="preordini-filtro-su-web"
                        label="Mostra solo articoli disponibili per PanifyCloud"
                        checked={onlySuWeb}
                        onChange={(e) => setOnlySuWeb(e.target.checked)}
                        className={`${darkMode ? 'text-light' : ''} mb-0 flex-grow-1`}
                      />
                      {hasActiveFilters && (
                        <small className="text-muted text-nowrap mb-0">
                          <i className="fas fa-filter me-1"></i>
                          Filtri attivi
                        </small>
                      )}
                    </div>
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <Form.Check
                        type="switch"
                        id="preordini-filtro-su-flash"
                        label="Mostra solo prodotti in promozione"
                        checked={onlySuFlash}
                        onChange={(e) => setOnlySuFlash(e.target.checked)}
                        className={`${darkMode ? 'text-light' : ''} mb-0 flex-grow-1`}
                      />
                      {hasActiveFilters && (
                        <div className="d-flex flex-wrap justify-content-end gap-1">
                          {searchFilter && <Badge bg="primary">Descrizione: "{searchFilter}"</Badge>}
                          {codiceFilter && <Badge bg="info">Codice: "{codiceFilter}"</Badge>}
                          {onlySuWeb && <Badge bg="warning" text="dark">PanifyCloud</Badge>}
                          {onlySuFlash && <Badge bg="danger">Promozione</Badge>}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Row className="mt-3">
                    <Col>
                      <Button 
                        variant="primary" 
                        onClick={loadPreordini}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Caricamento...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-sync-alt me-2"></i>
                            Aggiorna
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Contenuto principale */}
              {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                  <div className="text-center">
                    <Spinner animation="border" variant="primary" className="mb-3" />
                    <p>Caricamento preordini in corso...</p>
                  </div>
                </div>
              ) : error ? (
                <Alert variant="danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
              ) : (
                <Card>
                  <Card.Header className="card-header-sidebar-bg">
                    <h5 className="mb-0">
                      <i className="fas fa-list me-2"></i>
                      Articoli Raggruppati ({filteredPreordini.length})
                    </h5>
                  </Card.Header>
                  <Card.Body className={darkMode ? 'text-light' : ''}>
                    {filteredPreordini.length === 0 ? (
                      <div className="text-center py-4">
                        <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                        <p className="text-muted">Nessun preordine trovato per i criteri selezionati.</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table 
                          striped 
                          bordered 
                          hover 
                          className={darkMode ? 'table-dark' : ''}
                        >
                          <thead className={darkMode ? 'table-dark' : 'table-light'}>
                            <tr>
                              <th>Codice</th>
                              <th>Descrizione</th>
                              <th>Unità</th>
                              <th>Quantità Totale</th>
                              <th>Gruppo 1</th>
                              <th>Gruppo 2</th>
                              <th>Gruppo 3</th>
                              <th>Teglie Info</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredPreordini.map((preordine, index) => (
                              <tr key={`${preordine.codice_prod}-${index}`}>
                                <td>
                                  <code className={`small ${darkMode ? 'text-warning' : 'text-primary'}`}>
                                    {preordine.codice_prod}
                                  </code>
                                </td>
                                <td>
                                  <strong>{preordine.descriz}</strong>
                                </td>
                                <td>
                                  {preordine.unita_m}
                                </td>
                                <td>
                                  <strong className="text-success">
                                    {formatNumber(preordine.quantita)}
                                  </strong>
                                </td>
                                <td>{preordine.nome_g1 || '-'}</td>
                                <td>{preordine.nome_g2 || '-'}</td>
                                <td>{preordine.nome_g3 || '-'}</td>
                                <td>
                                  <small className="text-muted">
                                    {formatTeglieInfo(preordine.teglie_info)}
                                  </small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

            </Col>
          </Row>
        </Container>
      ) : (
        // ===== VERSIONE MOBILE: header + filtri fissi, solo cards scrollabili =====
        <div
          className="preordini-mobile-shell"
          style={{
            backgroundColor: darkMode ? '#121212' : '#FFFEF9',
          }}
        >
          <div
            className="preordini-mobile-sticky px-3 pt-2 pb-2"
            style={{
              backgroundColor: darkMode ? '#121212' : '#FFFEF9',
              borderBottom: `1px solid ${darkMode ? '#495057' : '#dee2e6'}`,
              boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.06)',
            }}
          >
            <div className={`d-flex justify-content-between align-items-center mb-2 ${darkMode ? 'text-light' : 'text-dark'}`}>
              <div style={{ minWidth: 0 }}>
                <h4 className="mb-0">
                  <i className="fas fa-clipboard-list me-2"></i>
                  Lista di Produzione
                </h4>
                {periodo && (
                  <small className={darkMode ? 'text-light' : 'text-muted'}>
                    {formatDate(periodo.data_inizio)} - {formatDate(periodo.data_fine)}
                  </small>
                )}
              </div>
              <Button
                variant="primary"
                onClick={loadPreordini}
                disabled={loading}
                className="d-flex align-items-center flex-shrink-0 ms-2"
              >
                {loading ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  <>
                    <i className="fas fa-sync-alt me-1"></i>
                    Aggiorna
                  </>
                )}
              </Button>
            </div>

            <Row className="g-2 mb-2">
              <Col xs={6}>
                <Form.Control
                  type="date"
                  value={filtri.giorno_esatto}
                  onChange={(e) => handleDateChange('giorno_esatto', e.target.value)}
                  className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                  aria-label="Data inizio"
                />
              </Col>
              <Col xs={6}>
                <Form.Control
                  type="date"
                  value={filtri.giorno_fine}
                  onChange={(e) => handleDateChange('giorno_fine', e.target.value)}
                  className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                  aria-label="Data fine"
                />
              </Col>
            </Row>

            <InputGroup className="mb-2">
              <InputGroup.Text className={darkMode ? 'bg-dark text-light border-secondary' : ''}>
                <i className="fas fa-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Cerca descrizione o codice..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className={darkMode ? 'bg-dark text-light border-secondary' : ''}
              />
              {searchFilter && (
                <Button
                  variant="outline-secondary"
                  onClick={() => { setSearchFilter(''); setCodiceFilter(''); }}
                  className={darkMode ? 'border-secondary text-light' : ''}
                >
                  <i className="fas fa-times"></i>
                </Button>
              )}
            </InputGroup>

            <div className="mt-1">
              <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                <Form.Check
                  type="switch"
                  id="mobile-preordini-filtro-su-web"
                  label="Solo articoli PanifyCloud"
                  checked={onlySuWeb}
                  onChange={(e) => setOnlySuWeb(e.target.checked)}
                  className={`${darkMode ? 'text-light' : ''} mb-0 flex-grow-1`}
                  style={{ minWidth: 0 }}
                />
                {hasActiveFilters && (
                  <small className="text-muted text-nowrap mb-0">
                    <i className="fas fa-filter me-1"></i>
                    Filtri attivi
                  </small>
                )}
              </div>
              <div className="d-flex align-items-center justify-content-between gap-2">
                <Form.Check
                  type="switch"
                  id="mobile-preordini-filtro-su-flash"
                  label="Solo prodotti in promozione"
                  checked={onlySuFlash}
                  onChange={(e) => setOnlySuFlash(e.target.checked)}
                  className={`${darkMode ? 'text-light' : ''} mb-0 flex-grow-1`}
                  style={{ minWidth: 0 }}
                />
                {hasActiveFilters && (
                  <div className="d-flex flex-wrap justify-content-end gap-1">
                    {searchFilter && <Badge bg="primary">Descrizione</Badge>}
                    {codiceFilter && <Badge bg="info">Codice</Badge>}
                    {onlySuWeb && <Badge bg="warning" text="dark">PanifyCloud</Badge>}
                    {onlySuFlash && <Badge bg="danger">Promozione</Badge>}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="preordini-mobile-scroll px-3 py-2">
            {loading && (
              <div className="text-center my-4">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Caricamento...</span>
                </Spinner>
                <p className="mt-2">Caricamento preordini...</p>
              </div>
            )}

            {error && (
              <Alert variant="danger" className="mb-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            )}

            {!loading && !error && (
              <>
                {filteredPreordini.length === 0 ? (
                  <Alert variant="info" className="text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    Nessun preordine trovato
                  </Alert>
                ) : (
                  <>
                    <div>
                      {filteredPreordini.map((preordine, index) => (
                        <Card key={`${preordine.codice_prod}-${index}`} className={`mb-2 ${darkMode ? 'text-light' : ''}`}>
                          <Card.Body className="p-2 py-2">
                            <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                              <h6
                                className="mb-0 flex-grow-1"
                                style={{ fontSize: '17px', fontWeight: 'bold', minWidth: 0 }}
                              >
                                <i className="fas fa-cube me-1 text-primary" style={{ fontSize: '15px' }}></i>
                                {preordine.descriz}
                              </h6>
                              <div className="text-end flex-shrink-0">
                                <strong className="text-success" style={{ fontSize: '24px', lineHeight: 1.1 }}>
                                  {formatNumber(preordine.quantita ?? preordine.quantita_totale)}
                                </strong>
                                <span className="ms-1" style={{ fontSize: '15px' }}>
                                  {preordine.unita_m}
                                </span>
                              </div>
                            </div>

                            <code
                              className={`${darkMode ? 'text-warning' : 'text-primary'}`}
                              style={{ fontSize: '13px' }}
                            >
                              {preordine.codice_prod}
                            </code>

                            <div className="mb-1 mt-1">
                              <span style={{ fontSize: '14px' }}>
                                <i className="fas fa-tags me-1 text-info" style={{ fontSize: '13px' }}></i>
                                <strong>Gruppi:</strong>
                                <span className={darkMode ? 'text-light' : 'text-muted'} style={{ opacity: 0.9 }}>
                                  {' '}{[preordine.nome_g1, preordine.nome_g2, preordine.nome_g2].filter(Boolean).join(' • ') || '-'}
                                </span>
                              </span>
                            </div>

                            {preordine.teglie_info && (
                              <div className="mb-0">
                                <span className={darkMode ? 'text-light' : 'text-muted'} style={{ fontSize: '13px', opacity: 0.9 }}>
                                  <i className="fas fa-info-circle me-1" style={{ fontSize: '12px' }}></i>
                                  <span><strong>Teglie:</strong> {formatTeglieInfo(preordine.teglie_info)}</span>
                                </span>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      ))}
                    </div>

                    <div className={`mt-3 p-3 rounded ${darkMode ? 'bg-secondary text-light' : 'bg-light text-muted'}`}>
                      <div className="text-center">
                        <small>
                          <i className="fas fa-info-circle me-1"></i>
                          <strong>Totale: {preordini.length}</strong>
                          <span className="ms-2">
                            <i className="fas fa-sum me-1 text-success"></i>
                            Quantità: {formatNumber(filteredPreordini.reduce((sum, preordine) => sum + parseFloat(preordine.quantita_totale || 0), 0))}
                          </span>
                        </small>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Preordini;
